import "./CustomerDetailPage.css";
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useOrganization } from "../organization/OrganizationProvider";

type CustomerData = {
  id: string;
  organization_id: string;
  customer_type: string;
  document_type: string | null;
  document_number: string | null;
  name: string;
  contact_name: string | null;
  email: string | null;
  phone: string | null;
  whatsapp: string | null;
  address: string | null;
  city: string | null;
  notes: string | null;
  preferred_contact: string | null;
  status: string;
  last_contact_at: string | null;
};

type AssetRow = {
  id: string;
  asset_type: string;
  brand: string | null;
  model: string | null;
  serial_number: string | null;
  identifier: string | null;
  status: string;
  updated_at: string;
};

type InteractionRow = {
  id: string;
  interaction_type: string;
  subject: string | null;
  notes: string | null;
  interaction_at: string;
  created_by_email: string | null;
};

type ServiceOrderRow = {
  id: string;
  order_number: string;
  status: string;
  priority: string;
  final_total: number | string;
  received_at: string;
  updated_at: string;
};

type QuotationRow = {
  id: string;
  quotation_number: string;
  status: string;
  total: number | string;
  issue_date: string;
  updated_at: string;
};

type SaleRow = {
  id: string;
  sale_number: string;
  status: string;
  total: number | string;
  paid_total: number | string;
  currency_code: string;
  issue_date: string;
  updated_at: string;
};

type CustomerDetail = {
  customer: CustomerData;
  assets: AssetRow[];
  interactions: InteractionRow[];
  service_orders: ServiceOrderRow[];
  quotations: QuotationRow[];
  sales: SaleRow[];
};

type CustomerEditForm = {
  customerType: "person" | "company";
  name: string;
  documentType: string;
  documentNumber: string;
  contactName: string;
  email: string;
  phone: string;
  whatsapp: string;
  address: string;
  city: string;
  countryCode: string;
  notes: string;
  preferredContact: "" | "whatsapp" | "phone" | "email";
};

function money(value: number | string) {
  return new Intl.NumberFormat("es-PE", {
    style: "currency",
    currency: "PEN",
  }).format(Number(value || 0));
}

function dateTime(value: string | null) {
  if (!value) return "—";
  return new Intl.DateTimeFormat("es-PE", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

const TYPE_LABELS: Record<string, string> = {
  call: "Llamada",
  email: "Correo",
  whatsapp: "WhatsApp",
  meeting: "Reunión",
  note: "Nota",
  service: "Servicio",
  visit: "Visita",
  other: "Otro",
};

export function CustomerDetailPage() {
  const { customerId = "" } = useParams();
  const { selectedOrganization } = useOrganization();

  const [detail, setDetail] = useState<CustomerDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState<CustomerEditForm | null>(null);

  const [interactionType, setInteractionType] = useState("whatsapp");
  const [subject, setSubject] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);

  const loadDetail = useCallback(async () => {
    if (!customerId) {
      setLoadError("Cliente inválido.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setLoadError(null);

    const { data, error } = await supabase.rpc("get_customer_detail", {
      p_customer_id: customerId,
    });

    if (error) {
      setDetail(null);
      setLoadError(error.message);
      setLoading(false);
      return;
    }

    const next = data as CustomerDetail;

    if (
      selectedOrganization &&
      next.customer.organization_id !== selectedOrganization.organizationId
    ) {
      setDetail(null);
      setLoadError("El cliente no pertenece a la empresa seleccionada.");
      setLoading(false);
      return;
    }

    setDetail(next);
    setLoading(false);
  }, [customerId, selectedOrganization]);

  useEffect(() => {
    void loadDetail();
  }, [loadDetail]);

  function openEdit(customer: CustomerData) {
    setEditForm({
      customerType:
        customer.customer_type === "company" ? "company" : "person",
      name: customer.name,
      documentType: customer.document_type ?? "",
      documentNumber: customer.document_number ?? "",
      contactName: customer.contact_name ?? "",
      email: customer.email ?? "",
      phone: customer.phone ?? "",
      whatsapp: customer.whatsapp ?? "",
      address: customer.address ?? "",
      city: customer.city ?? "",
      countryCode: "PE",
      notes: customer.notes ?? "",
      preferredContact:
        customer.preferred_contact === "whatsapp" ||
        customer.preferred_contact === "phone" ||
        customer.preferred_contact === "email"
          ? customer.preferred_contact
          : "",
    });
    setEditOpen(true);
    setActionError(null);
    setActionSuccess(null);
  }

  async function saveCustomer(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!editForm) return;

    if (!editForm.name.trim()) {
      setActionError("Ingresa el nombre o razón social.");
      return;
    }

    setSaving(true);
    setActionError(null);
    setActionSuccess(null);

    const { error } = await supabase.rpc("update_organization_customer", {
      p_customer_id: customerId,
      p_customer_type: editForm.customerType,
      p_name: editForm.name,
      p_document_type: editForm.documentType.trim() || null,
      p_document_number: editForm.documentNumber.trim() || null,
      p_contact_name: editForm.contactName.trim() || null,
      p_email: editForm.email.trim() || null,
      p_phone: editForm.phone.trim() || null,
      p_whatsapp: editForm.whatsapp.trim() || null,
      p_address: editForm.address.trim() || null,
      p_city: editForm.city.trim() || null,
      p_country_code: editForm.countryCode.trim().toUpperCase() || "PE",
      p_notes: editForm.notes.trim() || null,
      p_preferred_contact: editForm.preferredContact || null,
    });

    setSaving(false);

    if (error) {
      const duplicate =
        error.code === "23505" ||
        error.message.toLowerCase().includes("customers_org_document_uq");

      setActionError(
        duplicate
          ? "Ya existe otro cliente con ese tipo y número de documento."
          : error.message,
      );
      return;
    }

    setEditOpen(false);
    setEditForm(null);
    setActionSuccess("Cliente actualizado correctamente.");
    await loadDetail();
  }

  async function changeCustomerStatus(customer: CustomerData) {
    const nextStatus = customer.status === "active" ? "inactive" : "active";

    setSaving(true);
    setActionError(null);
    setActionSuccess(null);

    const { error } = await supabase.rpc("set_customer_status", {
      p_customer_id: customer.id,
      p_status: nextStatus,
    });

    setSaving(false);

    if (error) {
      if (error.message.includes("CUSTOMER_HAS_OPEN_SERVICE_ORDERS")) {
        setActionError("No se puede desactivar: existen órdenes de taller abiertas.");
      } else if (error.message.includes("CUSTOMER_HAS_OPEN_SALES")) {
        setActionError("No se puede desactivar: existen ventas abiertas.");
      } else if (error.message.includes("CUSTOMER_HAS_OPEN_RECEIVABLES")) {
        setActionError("No se puede desactivar: existen cuentas por cobrar pendientes.");
      } else if (error.message.includes("CUSTOMER_HAS_OPEN_QUOTATIONS")) {
        setActionError("No se puede desactivar: existen cotizaciones en curso.");
      } else {
        setActionError(error.message);
      }
      return;
    }

    setActionSuccess(
      nextStatus === "active" ? "Cliente activado." : "Cliente desactivado.",
    );
    await loadDetail();
  }

  async function addInteraction(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!subject.trim() && !notes.trim()) {
      setActionError("Ingresa un asunto o una nota.");
      return;
    }

    setSaving(true);
    setActionError(null);
    setActionSuccess(null);

    const { error } = await supabase.rpc("add_customer_interaction", {
      p_customer_id: customerId,
      p_interaction_type: interactionType,
      p_subject: subject.trim() || null,
      p_notes: notes.trim() || null,
      p_interaction_at: new Date().toISOString(),
    });

    setSaving(false);

    if (error) {
      setActionError(error.message);
      return;
    }

    setSubject("");
    setNotes("");
    setActionSuccess("Interacción registrada.");
    await loadDetail();
  }

  if (loading) {
    return (
      <div className="page">
        <section className="empty-state">
          <strong>Cargando cliente…</strong>
        </section>
      </div>
    );
  }

  if (loadError || !detail) {
    return (
      <div className="page">
        <Link className="compact-button customer-detail-back" to="/clientes">
          ← Volver a Clientes
        </Link>
        <section className="empty-state empty-state-error">
          <strong>No se pudo abrir el cliente.</strong>
          <p>{loadError ?? "Cliente no disponible."}</p>
        </section>
      </div>
    );
  }

  const {
    customer,
    assets,
    interactions,
    service_orders,
    quotations,
    sales,
  } = detail;

  return (
    <div className="page">
      <div className="customer-detail-topline">
        <Link className="compact-button" to="/clientes">
          ← Volver a Clientes
        </Link>
        <div className="customer-detail-actions">
          <span
            className={
              customer.status === "active"
                ? "status-badge status-badge-approved"
                : "status-badge"
            }
          >
            {customer.status}
          </span>
          <button
            className="compact-button"
            type="button"
            disabled={saving}
            onClick={() => openEdit(customer)}
          >
            Editar cliente
          </button>
          <button
            className="compact-button"
            type="button"
            disabled={saving}
            onClick={() => void changeCustomerStatus(customer)}
          >
            {customer.status === "active" ? "Desactivar" : "Activar"}
          </button>
        </div>
      </div>

      <header className="page-header">
        <div>
          <span className="eyebrow">
            {customer.customer_type === "company" ? "Empresa" : "Persona"}
          </span>
          <h1>{customer.name}</h1>
          <p>
            {customer.document_type && customer.document_number
              ? `${customer.document_type} ${customer.document_number}`
              : "Sin documento registrado"}
          </p>
        </div>
      </header>

      {editOpen && editForm ? (
        <section className="panel-card customer-edit-panel">
          <div className="section-heading">
            <div>
              <span className="eyebrow">Edición</span>
              <h2>Datos del cliente</h2>
            </div>
          </div>

          <form className="interaction-form" onSubmit={saveCustomer}>
            <label>
              Tipo
              <select
                value={editForm.customerType}
                onChange={(event) =>
                  setEditForm((current) =>
                    current
                      ? {
                          ...current,
                          customerType: event.target.value as "person" | "company",
                        }
                      : current,
                  )
                }
              >
                <option value="person">Persona</option>
                <option value="company">Empresa</option>
              </select>
            </label>

            <label>
              Nombre / razón social
              <input
                required
                value={editForm.name}
                onChange={(event) =>
                  setEditForm((current) =>
                    current ? { ...current, name: event.target.value } : current,
                  )
                }
              />
            </label>

            <label>
              Tipo documento
              <input
                value={editForm.documentType}
                onChange={(event) =>
                  setEditForm((current) =>
                    current
                      ? { ...current, documentType: event.target.value }
                      : current,
                  )
                }
              />
            </label>

            <label>
              Número documento
              <input
                value={editForm.documentNumber}
                onChange={(event) =>
                  setEditForm((current) =>
                    current
                      ? { ...current, documentNumber: event.target.value }
                      : current,
                  )
                }
              />
            </label>

            <label>
              Contacto
              <input
                value={editForm.contactName}
                onChange={(event) =>
                  setEditForm((current) =>
                    current
                      ? { ...current, contactName: event.target.value }
                      : current,
                  )
                }
              />
            </label>

            <label>
              Contacto preferido
              <select
                value={editForm.preferredContact}
                onChange={(event) =>
                  setEditForm((current) =>
                    current
                      ? {
                          ...current,
                          preferredContact: event.target
                            .value as CustomerEditForm["preferredContact"],
                        }
                      : current,
                  )
                }
              >
                <option value="">Sin preferencia</option>
                <option value="whatsapp">WhatsApp</option>
                <option value="phone">Teléfono</option>
                <option value="email">Correo</option>
              </select>
            </label>

            <label>
              Correo
              <input
                type="email"
                value={editForm.email}
                onChange={(event) =>
                  setEditForm((current) =>
                    current ? { ...current, email: event.target.value } : current,
                  )
                }
              />
            </label>

            <label>
              Teléfono
              <input
                value={editForm.phone}
                onChange={(event) =>
                  setEditForm((current) =>
                    current ? { ...current, phone: event.target.value } : current,
                  )
                }
              />
            </label>

            <label>
              WhatsApp
              <input
                value={editForm.whatsapp}
                onChange={(event) =>
                  setEditForm((current) =>
                    current
                      ? { ...current, whatsapp: event.target.value }
                      : current,
                  )
                }
              />
            </label>

            <label>
              Ciudad
              <input
                value={editForm.city}
                onChange={(event) =>
                  setEditForm((current) =>
                    current ? { ...current, city: event.target.value } : current,
                  )
                }
              />
            </label>

            <label className="customer-detail-span">
              Dirección
              <input
                value={editForm.address}
                onChange={(event) =>
                  setEditForm((current) =>
                    current
                      ? { ...current, address: event.target.value }
                      : current,
                  )
                }
              />
            </label>

            <label>
              País
              <input
                maxLength={2}
                value={editForm.countryCode}
                onChange={(event) =>
                  setEditForm((current) =>
                    current
                      ? {
                          ...current,
                          countryCode: event.target.value.toUpperCase(),
                        }
                      : current,
                  )
                }
              />
            </label>

            <label className="customer-detail-span">
              Notas
              <textarea
                rows={3}
                value={editForm.notes}
                onChange={(event) =>
                  setEditForm((current) =>
                    current ? { ...current, notes: event.target.value } : current,
                  )
                }
              />
            </label>

            <div className="form-actions customer-detail-span">
              <button className="primary-button" type="submit" disabled={saving}>
                {saving ? "Guardando…" : "Guardar cambios"}
              </button>
              <button
                className="ghost-button"
                type="button"
                disabled={saving}
                onClick={() => {
                  setEditOpen(false);
                  setEditForm(null);
                }}
              >
                Cancelar
              </button>
            </div>
          </form>
        </section>
      ) : null}

      {actionError ? (
        <div className="inline-alert" role="alert">
          {actionError}
        </div>
      ) : null}

      {actionSuccess ? (
        <div className="form-success customer-detail-message">
          {actionSuccess}
        </div>
      ) : null}

      <section className="customer-detail-summary">
        <div className="metric-card">
          <span>Activos</span>
          <strong>{assets.length}</strong>
        </div>
        <div className="metric-card">
          <span>Órdenes</span>
          <strong>{service_orders.length}</strong>
        </div>
        <div className="metric-card">
          <span>Cotizaciones</span>
          <strong>{quotations.length}</strong>
        </div>
        <div className="metric-card">
          <span>Ventas</span>
          <strong>{sales.length}</strong>
        </div>
        <div className="metric-card">
          <span>Interacciones</span>
          <strong>{interactions.length}</strong>
        </div>
      </section>

      <section className="customer-detail-grid">
        <article className="panel-card">
          <div className="section-heading">
            <div>
              <span className="eyebrow">Contacto</span>
              <h2>Datos del cliente</h2>
            </div>
          </div>

          <dl className="customer-detail-definition">
            <div>
              <dt>Contacto</dt>
              <dd>{customer.contact_name ?? "—"}</dd>
            </div>
            <div>
              <dt>Preferido</dt>
              <dd>{customer.preferred_contact ?? "—"}</dd>
            </div>
            <div>
              <dt>WhatsApp</dt>
              <dd>{customer.whatsapp ?? "—"}</dd>
            </div>
            <div>
              <dt>Teléfono</dt>
              <dd>{customer.phone ?? "—"}</dd>
            </div>
            <div>
              <dt>Correo</dt>
              <dd>{customer.email ?? "—"}</dd>
            </div>
            <div>
              <dt>Ciudad</dt>
              <dd>{customer.city ?? "—"}</dd>
            </div>
            <div className="customer-detail-span">
              <dt>Dirección</dt>
              <dd>{customer.address ?? "—"}</dd>
            </div>
            <div className="customer-detail-span">
              <dt>Último contacto</dt>
              <dd>{dateTime(customer.last_contact_at)}</dd>
            </div>
          </dl>
        </article>

        <article className="panel-card">
          <div className="section-heading">
            <div>
              <span className="eyebrow">Seguimiento</span>
              <h2>Nueva interacción</h2>
            </div>
          </div>

          <form className="interaction-form" onSubmit={addInteraction}>
            <label>
              Tipo
              <select
                value={interactionType}
                onChange={(event) => setInteractionType(event.target.value)}
              >
                <option value="whatsapp">WhatsApp</option>
                <option value="call">Llamada</option>
                <option value="email">Correo</option>
                <option value="meeting">Reunión</option>
                <option value="visit">Visita</option>
                <option value="service">Servicio</option>
                <option value="note">Nota</option>
                <option value="other">Otro</option>
              </select>
            </label>

            <label>
              Asunto
              <input
                value={subject}
                onChange={(event) => setSubject(event.target.value)}
              />
            </label>

            <label className="customer-detail-span">
              Notas
              <textarea
                rows={4}
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
              />
            </label>

            <div className="form-actions customer-detail-span">
              <button className="primary-button" type="submit" disabled={saving}>
                {saving ? "Guardando…" : "Registrar interacción"}
              </button>
            </div>
          </form>
        </article>
      </section>

      <section className="panel-card">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Historial</span>
            <h2>Interacciones</h2>
          </div>
        </div>

        <div className="customer-interaction-list">
          {interactions.length === 0 ? (
            <p className="settings-empty">Sin interacciones registradas.</p>
          ) : (
            interactions.map((interaction) => (
              <article className="customer-interaction-row" key={interaction.id}>
                <span className="customer-interaction-type">
                  {TYPE_LABELS[interaction.interaction_type] ??
                    interaction.interaction_type}
                </span>
                <div>
                  <strong>{interaction.subject ?? "Sin asunto"}</strong>
                  <span>
                    {dateTime(interaction.interaction_at)}
                    {interaction.created_by_email
                      ? ` · ${interaction.created_by_email}`
                      : ""}
                  </span>
                  {interaction.notes ? <p>{interaction.notes}</p> : null}
                </div>
              </article>
            ))
          )}
        </div>
      </section>

      <section className="customer-detail-grid">
        <article className="panel-card">
          <div className="section-heading">
            <div>
              <span className="eyebrow">Activos</span>
              <h2>Bienes asociados</h2>
            </div>
            <Link className="compact-button" to="/activos">
              Ver módulo
            </Link>
          </div>

          <div className="customer-compact-list">
            {assets.length === 0 ? (
              <p className="settings-empty">Sin activos registrados.</p>
            ) : (
              assets.map((asset) => (
                <div className="customer-compact-row" key={asset.id}>
                  <div>
                    <strong>
                      {[asset.brand, asset.model].filter(Boolean).join(" ") ||
                        asset.asset_type}
                    </strong>
                    <span>
                      {asset.asset_type}
                      {asset.identifier ? ` · ${asset.identifier}` : ""}
                    </span>
                  </div>
                  <span className="status-badge">{asset.status}</span>
                </div>
              ))
            )}
          </div>
        </article>

        <article className="panel-card">
          <div className="section-heading">
            <div>
              <span className="eyebrow">Taller</span>
              <h2>Órdenes de servicio</h2>
            </div>
          </div>

          <div className="customer-compact-list">
            {service_orders.length === 0 ? (
              <p className="settings-empty">Sin órdenes registradas.</p>
            ) : (
              service_orders.map((order) => (
                <Link
                  className="customer-compact-row customer-compact-link"
                  key={order.id}
                  to={`/taller/${order.id}`}
                >
                  <div>
                    <strong>{order.order_number}</strong>
                    <span>
                      {dateTime(order.received_at)} · {money(order.final_total)}
                    </span>
                  </div>
                  <span className="status-badge">{order.status}</span>
                </Link>
              ))
            )}
          </div>
        </article>
      </section>

      <section className="panel-card">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Comercial</span>
            <h2>Cotizaciones</h2>
          </div>
          <Link className="compact-button" to="/cotizaciones">
            Ver módulo
          </Link>
        </div>

        <div className="customer-compact-list">
          {quotations.length === 0 ? (
            <p className="settings-empty">Sin cotizaciones registradas.</p>
          ) : (
            quotations.map((quotation) => (
              <Link
                className="customer-compact-row customer-compact-link"
                key={quotation.id}
                to={`/cotizaciones/${quotation.id}`}
              >
                <div>
                  <strong>{quotation.quotation_number}</strong>
                  <span>
                    {quotation.issue_date} · {money(quotation.total)}
                  </span>
                </div>
                <span className="status-badge">{quotation.status}</span>
              </Link>
            ))
          )}
        </div>
      </section>

      <section className="panel-card">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Comercial</span>
            <h2>Ventas</h2>
          </div>
          <Link className="compact-button" to="/ventas">
            Ver módulo
          </Link>
        </div>

        <div className="customer-compact-list">
          {sales.length === 0 ? (
            <p className="settings-empty">Sin ventas registradas.</p>
          ) : (
            sales.map((sale) => (
              <Link
                className="customer-compact-row customer-compact-link"
                key={sale.id}
                to={`/ventas/${sale.id}`}
              >
                <div>
                  <strong>{sale.sale_number}</strong>
                  <span>
                    {sale.issue_date} · {money(sale.total)}
                    {Number(sale.paid_total) > 0
                      ? ` · Pagado ${money(sale.paid_total)}`
                      : ""}
                  </span>
                </div>
                <span className="status-badge">{sale.status}</span>
              </Link>
            ))
          )}
        </div>
      </section>
    </div>
  );
}
